import { PlusOutlined } from '@ant-design/icons';
import { ApolloQueryResult } from '@apollo/client';
import { Button, Modal, Tabs } from 'antd';
import { orderBy } from 'lodash';
import React, { useState } from 'react';

import {
  AccessPointRoleTableFragment,
  AccessPointRowFragment,
  AccessPointType_enum,
  getAccessPointRolesQuery,
  PermissionKey_enum,
  PermissionScope_enum,
  RoleWithStaffPermissionsFragment,
} from '../../../graphql/hasura/generated';
import { AccessPointRoleModel } from '../../AccessPointRole/model';
import { PERMISSION_SCOPE_WEIGHTS } from '../../helpers/permission-scopes';

import { AccessPointRoleForm } from './AccessPointRoleForm';
import {
  AccessPointRoleTable,
  IAccessPointRoleEditArgs,
  IAccessPointRoleRow,
} from './AccessPointRoleTable';

interface IAccessPointRoleManagerProps {
  accessPoint: AccessPointRowFragment;
  accessPointRoles: AccessPointRoleTableFragment[];
  roles: RoleWithStaffPermissionsFragment[];
  readOnly?: boolean;
  refetch(): Promise<ApolloQueryResult<getAccessPointRolesQuery>>;
}

interface IScreenState {
  screen: 'TABLE' | 'FORM';
  formProps?: {
    isNew: boolean;
  } & IAccessPointRoleEditArgs;
}

export const AccessPointRoleManager: React.FC<IAccessPointRoleManagerProps> = (props) => {
  const { accessPoint, accessPointRoles, refetch } = props;
  const readOnly = props.readOnly === true;

  const [tabKey, setTabKey] = useState<'MANUAL' | 'AUTO'>('MANUAL');
  const [visible, setVisible] = useState(false);
  const [screenState, setScreenState] = useState<IScreenState>({ screen: 'TABLE' });

  const isDenylist = accessPoint.type === AccessPointType_enum.RESTRICTED_BLACKLIST;
  const isAllowlist = accessPoint.type === AccessPointType_enum.RESTRICTED_WHITELIST;

  const sortedRoles = orderBy(props.roles, [
    r => r.organization?.name,
    r => PERMISSION_SCOPE_WEIGHTS[r.permissionScope],
    r => r.name,
  ], ['asc', 'asc']);

  const autoAllowlistedRoles = sortedRoles.filter(r => (
    r.permissionScope === PermissionScope_enum.ACCESS_POINT ||
    r.rolePermissions.some(rp => rp.permission.key === PermissionKey_enum.AccessPoint_StaffAccess)
  ));

  const accessPointRoleRows = accessPointRoles.map(accessPointRole => {
    const role = sortedRoles.find(r => r.roleId === accessPointRole.role.roleId);

    if (!role) {
      return null;
    }

    return { role, accessPointRole };
  })
  .filter(Boolean)
  .filter(a => !!a?.accessPointRole.hasAccess === !isDenylist) as IAccessPointRoleRow[];

  const manualRoleRows = accessPointRoleRows.filter(a => (
    !autoAllowlistedRoles.some(r => r.roleId === a.role.roleId)
  ));

  const autoAllowlistedRoleRows = autoAllowlistedRoles.map(role => ({
    role,
    accessPointRole: accessPointRoleRows.find(a => a.role.roleId === role.roleId)?.accessPointRole,
  }));

  function onEditRow({ accessPointRole, selectedRoleId }: IAccessPointRoleEditArgs) {
    setScreenState({
      screen: 'FORM',
      formProps: {
        accessPointRole,
        selectedRoleId,
        isNew: accessPointRole ? false : true,
      },
    });
  }

  let modalScreen = null;

  if (screenState.screen === 'TABLE') {
    const manualRolesScreen = (
      <>
        {!readOnly && AccessPointRoleModel.permissions.canCreate() && (
            <Button
              type='dashed'
              style={{ width: '100%', margin: '24px 0' }}
              icon={<PlusOutlined />}
              onClick={() => {
                setScreenState({
                  screen: 'FORM',
                  formProps: { isNew: true },
                });
              }}
            >
              Add Role
            </Button>
        )}
        {accessPoint.type === AccessPointType_enum.COMMON_AREA && (
          <div className='help-text' style={{ marginBottom: 25 }}>
            All roles with property access will automatically be allowed to unlock Common Areas.
            Roles only need to be manually configured for Common Areas to either
            set a custom schedule or enable cross-property access.
          </div>
        )}
        <AccessPointRoleTable
          rows={manualRoleRows}
          onEditRow={onEditRow}
          refetch={refetch}
          readOnly={readOnly}
          filterColumns={isDenylist ? c => c.title !== 'Schedule' : undefined}
        />
      </>
    );

    modalScreen = (
      <>
        {isAllowlist && (
          <Tabs
            animated={false}
            activeKey={tabKey}
            onChange={nextActiveTab => setTabKey(nextActiveTab as any)}
          >
            <Tabs.TabPane
              tab='Manually Allowed'
              key='MANUAL'
            >
              {manualRolesScreen}
            </Tabs.TabPane>
            <Tabs.TabPane tab='Automatically Allowed' key='AUTO'>
              <AccessPointRoleTable
                rows={autoAllowlistedRoleRows}
                onEditRow={onEditRow}
                refetch={refetch}
                readOnly={readOnly}
                filterColumns={c => c.title !== 'Unit Group'}
              />
            </Tabs.TabPane>
          </Tabs>
        )}
        {!isAllowlist && manualRolesScreen}
      </>
    );
  } else {
    modalScreen = (
      <AccessPointRoleForm
        isNew={!!screenState.formProps?.isNew}
        accessPoint={accessPoint}
        hasAccess={!isDenylist}
        selectedRoleId={screenState.formProps?.selectedRoleId}
        accessPointRole={screenState.formProps?.accessPointRole}
        roles={sortedRoles}
        onCancel={() => setScreenState({ screen: 'TABLE' })}
        onSubmit={() => setScreenState({ screen: 'TABLE' })}
      />
    );
  }

  return (
    <>
      <div style={{ lineHeight: 'normal', marginBottom: 10 }}>
        {!isDenylist && (
          <>
            <div>
              <strong>{manualRoleRows.length}</strong> roles manually {isAllowlist ? 'allowed' : 'configured'}
            </div>
            {isAllowlist && (
              <div><strong>{autoAllowlistedRoleRows.length}</strong> roles automatically allowed</div>
            )}
          </>
        )}
        {isDenylist && (
          <div><strong>{manualRoleRows.length}</strong> roles denied</div>
        )}
      </div>
      <div style={{ lineHeight: 'normal' }}>
        <a
          onClick={(e) => {
            e.preventDefault();
            setVisible(true);
          }}
        >
          {readOnly ? 'View' : 'Manage'} Roles
        </a>
      </div>
      <Modal
        title={`${readOnly ? '' : 'Manage '}${isDenylist ? 'Denied' : 'Allowed'} Roles`}
        visible={visible}
        maskClosable
        width='75%'
        footer={null}
        onCancel={() => setVisible(false)}
      >
        {modalScreen}
      </Modal>
    </>
  );
};
